import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { getCityPackageAmountApi } from "src/api/invoiceApi";
import FormikInput from "src/components/FormikInput ";
import { notification } from "src/utils/messages";
import * as Yup from "yup";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import DatePicker from "@mui/lab/DatePicker";
import moment from "moment";
import { getProductApi } from "src/api/productApi";
import Label from "../components/Label";
import { formatExpiryExtension, getDefaultExpiryDate } from "src/utils/common";

export default function InvoiceAmountVerifiedModal({
  modal = false,
  toggleModal = () => { },
  onClose = () => { },
  data,
  setData,
  loginUser
}) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 10,
    borderRadius: "10px",
    p: 2
  };

  const [packageAmountData, setPackageAmountData] = useState({});
  const [productItems, setProductItems] = useState([]);

  const isRenewal = data?.is_renewal === true || data?.l_type == 2;
  const isExpiryEdit = data?.isExpiryEdit === true;
  const showExpiry = data?.showExpiry === true;
  const joiningDate = (isRenewal
    ? data?.membership_expiry_date
    : data?.joining_date) || data?.payment_date || moment().format("DD-MM-YYYY");
  const currentDueDate = data?.due_date || data?.Due_date;
  const expectedDueDate = getDefaultExpiryDate(
    data?.membership_expiry_date || data?.payment_date || joiningDate,
    isRenewal ? data?.previous_due_date || currentDueDate : null
  );
  const adminLimitDate = moment(expectedDueDate, "DD-MM-YYYY");
  const canEditExpiry =
    loginUser?.u_type == 0 ||
    (loginUser?.u_type == 2 && loginUser?.management_p == 1);
  const isCityPartner = loginUser?.u_type == 1;

  const handleCloseModal = () => {
    // Call onClose callback to revert card position
    if (onClose) {
      onClose();
    }
    toggleModal();
  };

  useEffect(() => {
    getCityPackageAmount({ l_allocated_to: data?.l_allocated_to });
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const resp = await getProductApi({ i_p_type: 1 });
    if (resp.status === 1) {
      setProductItems(resp.data);
    }
  };

  const getCityPackageAmount = async (data) => {
    let resp = await getCityPackageAmountApi(data);

    let notify = notification({
      type: resp.status == 0 ? "error" : "success",
      message: resp.message
    });
    notify();

    if (resp.status) {
      setPackageAmountData(resp?.data);
    }
  };

  const validationSchema = Yup.object({
    i_amount: isExpiryEdit
      ? Yup.number()
      : Yup.number().required("Invoice Amount is required"),
    base_i_amount: isExpiryEdit
      ? Yup.number()
      : Yup.number().when("i_amount", {
        is: 0,
        then: Yup.number()
          .required("Base Membership Amount is required")
          .moreThan(0, "Base Membership Amount must be greater than 0")
      }),
    payment_mode: isExpiryEdit
      ? Yup.number()
      : Yup.number().required("Payment method is required"),
    payment_date: isExpiryEdit
      ? Yup.string()
      : Yup.string().required("Payment date is required"),
    invoice_expiry_date: isExpiryEdit
      ? Yup.string()
      : Yup.string().required("Invoice Expiry date is required"),
    i_item_id: isExpiryEdit
      ? Yup.number()
      : Yup.number().required("Product selection is required"),
    expiry_date: isExpiryEdit || showExpiry
      ? Yup.string().required("Expiry date is required")
      : Yup.string()
  });

  return (
    <Modal
      open={modal}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          style={{ marginBottom: "5px", textAlign: "center" }}
        >
          {isExpiryEdit
            ? "Adjust Expiry"
            : showExpiry
              ? "Invoice Summary & Expiry Adjustment"
              : "Invoice Summary"}
        </Typography>
        <hr />

        <Formik
          enableReinitialize={true}
          initialValues={{
            i_amount:
              (data?.i_amount && data?.i_amount !== "0")
                ? data?.i_amount
                : (data?.l_type === 2
                  ? packageAmountData?.m_r_fee
                  : packageAmountData?.n_m_fee || "0"),
            base_i_amount: data?.base_i_amount === 0
              ? (data?.i_amount && data?.i_amount !== "0")
                ? data?.i_amount
                : (data?.l_type == 2
                  ? packageAmountData?.m_r_fee
                  : packageAmountData?.n_m_fee || "0")
              : data?.base_i_amount,
            payment_mode: data.payment_mode || 0,
            payment_date: data.payment_date
              ? moment(data.payment_date, "DD-MM-YYYY").toDate()
              : new Date(),
            invoice_expiry_date: data.invoice_expiry_date
              ? moment(data.invoice_expiry_date, "DD-MM-YYYY").toDate()
              : moment(data.payment_date, "DD-MM-YYYY").add(30, "days").toDate(),
            i_item_id: data.i_item_id || "",
            expiry_date: data.expiry_date
              ? moment(data.expiry_date, "DD-MM-YYYY").toDate()
              : currentDueDate
                ? moment(currentDueDate, "DD-MM-YYYY").toDate()
                : moment(expectedDueDate, "DD-MM-YYYY").toDate()
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setData({ ...data, ...values, isSubmit: true });
            toggleModal();
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ mt: "5px" }}>
                {!isExpiryEdit && (
                  <>
                    <Grid item xs={12}>
                      <FormikInput name="i_amount" label="Invoice Amount (With GST)" decimalOnly required />
                    </Grid>

                    {values.i_amount == 0 && (
                      <Grid item xs={12}>
                        <FormikInput name="base_i_amount" label="Base Membership Amount" decimalOnly required />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <FormikInput
                        name="i_item_id"
                        label="Select Product"
                        select
                        required
                        options={productItems?.map((item) => ({
                          label: item.i_p_name,
                          value: item.i_p_id
                        }))}
                      />
                    </Grid>

                    <Grid item xs={12} className="mb-15">
                      <Stack spacing={10}>
                        <FormControl>
                          <FormLabel id="demo-error-radios" className="font-bold">
                            Please Select Payment Method{" "}
                            <span
                              aria-hidden="true"
                              className="MuiInputLabel-asterisk MuiFormLabel-asterisk css-1szckaa-MuiFormLabel-asterisk"
                            >
                              *
                            </span>
                          </FormLabel>

                          <RadioGroup
                            row // display as Row
                            name="payment_mode"
                            value={values?.payment_mode}
                            required
                            onChange={(e) => {
                              setFieldValue("payment_mode", e.target.value);
                            }}
                          >
                            <FormControlLabel value={0} control={<Radio />} label="Cash" />
                            <FormControlLabel value={1} control={<Radio />} label="Transfer" />
                            <FormControlLabel value={2} control={<Radio />} label="Cheque" />
                            <FormControlLabel value={3} control={<Radio />} label="None" />
                          </RadioGroup>
                        </FormControl>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} className="mb-15">
                      <Stack spacing={10}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            type="date"
                            name="payment_date"
                            label="Payment Date"
                            value={
                              values?.payment_date !== undefined
                                ? moment(values.payment_date, "DD-MM-YYYY").toDate()
                                : null
                            }
                            inputFormat="dd/MM/yyyy"
                            onChange={(newDate) => {
                              setFieldValue(
                                "payment_date",
                                moment(newDate).format("DD-MM-YYYY")
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                // disabled
                                {...params}
                                required
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} className="mb-15">
                      <Stack spacing={10}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            type="date"
                            name="invoice_expiry_date"
                            label="Invoice Expiry Date"
                            value={
                              values?.invoice_expiry_date !== undefined
                                ? moment(values.invoice_expiry_date, "DD-MM-YYYY").toDate()
                                : null
                            }
                            inputFormat="dd/MM/yyyy"
                            onChange={(newDate) => {
                              setFieldValue(
                                "invoice_expiry_date",
                                moment(newDate).format("DD-MM-YYYY")
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Stack>
                    </Grid>
                  </>
                )}

                {isExpiryEdit || showExpiry ? (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        label="Joining Date"
                        value={joiningDate}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6} className="mb-15">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          type="date"
                          name="expiry_date"
                          label="Expiry Date"
                          value={
                            values?.expiry_date !== undefined
                              ? moment(values.expiry_date, "DD-MM-YYYY").toDate()
                              : null
                          }
                          inputFormat="dd/MM/yyyy"
                          minDate={moment(joiningDate, "DD-MM-YYYY").toDate()}
                          maxDate={isCityPartner ? adminLimitDate.toDate() : undefined}
                          disabled={!canEditExpiry && !isCityPartner}
                          onChange={(newDate) => {
                            setFieldValue(
                              "expiry_date",
                              moment(newDate).format("DD-MM-YYYY")
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              onKeyDown={
                                isCityPartner || !canEditExpiry
                                  ? (e) => e.preventDefault()
                                  : undefined
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <span style={{ fontWeight: "bold" }}>Extension :</span>
                        {(() => {
                          const counter = formatExpiryExtension(
                            values?.expiry_date
                              ? moment(values.expiry_date, "DD-MM-YYYY").format("DD-MM-YYYY")
                              : expectedDueDate,
                            expectedDueDate
                          );
                          if (counter.tone == "success")
                            return <Label color="success">{counter.text}</Label>;
                          if (counter.tone == "error")
                            return <Label color="error">{counter.text}</Label>;
                          return (
                            <>
                              <Label color="default">{counter.text}</Label>
                              <span style={{ color: "#838383" }}>(00)</span>
                            </>
                          );
                        })()}
                      </Stack>
                      {isCityPartner && (
                        <Typography variant="caption" color="text.secondary">
                          City Partner limit: {adminLimitDate.format("DD-MM-YYYY")} (cannot
                          extend beyond)
                        </Typography>
                      )}
                    </Grid>
                  </>
                ) : null}

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 1 }}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      Close
                    </Button>

                    <Button variant="contained" type="submit">
                      {isExpiryEdit ? "Update Expiry" : "Submit"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
}
